import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useRealtimeFrames, useRealtimeResync } from '@/lib/realtime'

import { notificationsApi } from '../api'
import { isInboxNotification, isUnread, parseNotification } from '../types'

export const UNREAD_POLL_INTERVAL_MS = 15_000

interface UnreadNotificationCount {
  count: number
  refresh: () => void
}

interface UnreadNotificationCountOptions {
  /** Test seam; production always uses the 15 s fallback. */
  intervalMs?: number
}

/**
 * Discovery-header bell / shell badge count (UI_PARITY §3.6).
 *
 * Refreshes on mount, on route changes, when the window regains focus, every
 * 15 seconds only while the document is visible, and immediately on a
 * user-channel `notification` frame. Reconnect/focus refetches close Pub/Sub
 * gaps; failures keep the last known count.
 */
export function useUnreadNotificationCount(
  { intervalMs = UNREAD_POLL_INTERVAL_MS }: UnreadNotificationCountOptions = {},
): UnreadNotificationCount {
  const [count, setCount] = useState(0)
  const [attempt, setAttempt] = useState(0)
  const { pathname } = useLocation()

  const refresh = useCallback(() => {
    setAttempt((value) => value + 1)
  }, [])

  useRealtimeFrames((frame) => {
    if (frame.type !== 'notification') return
    const notification = parseNotification(frame.notification)
    if (
      !notification ||
      !isInboxNotification(notification) ||
      !isUnread(notification)
    ) {
      return
    }
    // Immediate badge feedback; the REST read below is authoritative and
    // also counts notifications that arrived while the socket was down.
    setCount((current) => current + 1)
    refresh()
  })

  useRealtimeResync(refresh)

  useEffect(() => {
    let cancelled = false
    let controller: AbortController | null = null

    async function load() {
      if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'hidden'
      ) {
        return
      }
      controller?.abort()
      controller = new AbortController()
      try {
        const response = await notificationsApi.unreadCount(controller.signal)
        if (cancelled) return
        const next = Math.max(0, Math.trunc(response.count))
        if (Number.isFinite(next)) setCount(next)
      } catch {
        // Keep the previous badge value; the refresh path will retry.
      }
    }

    void load()

    const timer = window.setInterval(() => {
      void load()
    }, intervalMs)

    const onFocus = () => {
      void load()
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void load()
    }

    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      controller?.abort()
      window.clearInterval(timer)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [attempt, intervalMs, pathname])

  return { count, refresh }
}
